/*
 * PROD-7828 A-15 — pure rules for the teacher app's Earnings switch (teacher_payment_profile.earnings_visible):
 * 1 = the teacher sees Earnings, 0 or no profile row = hidden. Used by the Overview bulk bar and the Payment
 * details toggle. Mirrors goserver earnings_visible_plan.go (500 teachers per request, reason required).
 */

export const MAX_EARNINGS_TEACHERS = 500;
export const MAX_REASON_LEN = 1024;

// Overview filter: '' = every teacher (never sent); '1' / '0' are the server's earnings_visible values.
export const EARNINGS_FILTER_OPTIONS = [
  { value: '1', label: 'Earnings visible' },
  { value: '0', label: 'Earnings hidden' }
];
const EARNINGS_FILTER_VALUES = EARNINGS_FILTER_OPTIONS.map((o) => o.value);

export const earnings_filter_param = (value) =>
  EARNINGS_FILTER_VALUES.indexOf(String(value ?? '')) === -1 ? '' : String(value);

export const is_earnings_visible = (row) => Number(row && row.earnings_visible) === 1;

export const earnings_pill = (row) =>
  is_earnings_visible(row)
    ? { tone: 'green', label: 'Visible' }
    : { tone: 'gray', label: 'Hidden' };

// ---- selection (teacher_uuid -> true), kept across pages until cleared ----

export const toggle_pick = (picked, uuid) => {
  const next = { ...picked };
  if (next[uuid]) delete next[uuid];
  else next[uuid] = true;
  return next;
};

// The header box: every row on the page picked -> unpick them; otherwise pick them all (other pages kept).
export const toggle_page = (picked, rows) => {
  const uuids = (rows || []).map((r) => r.teacher_uuid);
  const all = uuids.length > 0 && uuids.every((u) => picked[u]);
  const next = { ...picked };
  uuids.forEach((u) => {
    if (all) delete next[u];
    else next[u] = true;
  });
  return next;
};

export const page_pick_state = (picked, rows) => {
  const uuids = (rows || []).map((r) => r.teacher_uuid);
  const n = uuids.filter((u) => picked[u]).length;
  return { all: uuids.length > 0 && n === uuids.length, some: n > 0 && n < uuids.length };
};

export const picked_uuids = (picked) => Object.keys(picked || {}).filter((u) => picked[u]);

// ---- request ----

export const earnings_reason_error = (reason) => {
  const r = (reason || '').trim();
  if (!r) return 'A reason is required. It goes to the payment audit trail.';
  if (r.length > MAX_REASON_LEN) return `The reason must be at most ${MAX_REASON_LEN} characters.`;
  return '';
};

export const bulk_form_error = (uuids, reason) => {
  if (!uuids.length) return 'Select at least one teacher.';
  if (uuids.length > MAX_EARNINGS_TEACHERS)
    return `Select at most ${MAX_EARNINGS_TEACHERS} teachers at a time.`;
  return earnings_reason_error(reason);
};

export const bulk_payload = (uuids, visible, reason) => ({
  teacher_uuids: uuids,
  visible: !!visible,
  reason: (reason || '').trim()
});

export const single_payload = (teacher_uuid, visible, reason) => ({
  teacher_uuid,
  earnings_visible: visible ? 1 : 0,
  reason: (reason || '').trim()
});

export const bulk_action_label = (visible) =>
  visible ? 'Show Earnings to selected' : 'Hide Earnings from selected';

export const bulk_confirm_text = (visible, count) =>
  `${visible ? 'Show' : 'Hide'} the Earnings page ${visible ? 'to' : 'from'} ${count} ${
    count === 1 ? 'teacher' : 'teachers'
  } in the teacher app. No payment changes.`;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The toast after a bulk change: counts, then the staff teachers it refused by name.
export const bulk_result_text = (data) => {
  const d = data || {};
  const parts = [`${plural(Number(d.changed) || 0, 'teacher', 'teachers')} updated`];
  if (Number(d.unchanged)) parts.push(`${d.unchanged} already ${d.visible ? 'visible' : 'hidden'}`);
  if (Number(d.not_found)) parts.push(`${d.not_found} not found`);
  let text = `Earnings ${d.visible ? 'shown' : 'hidden'}: ${parts.join(', ')}.`;
  const refused = (Array.isArray(d.results) ? d.results : []).filter((r) => r.result === 'refused');
  if (refused.length) {
    const names = refused.map((r) => r.teacher_name || r.teacher_uuid).join(', ');
    text += ` Not changed (staff accounts, Earnings is not available): ${names}.`;
  }
  return text;
};

// Hridaya's notification has success/error only: error when something was refused and nothing else moved.
export const bulk_result_type = (data) => {
  const d = data || {};
  const failed = (Number(d.refused) || 0) + (Number(d.not_found) || 0);
  return failed && !Number(d.changed) && !Number(d.unchanged) ? 'error' : 'success';
};

// ---- Payment details card ----

// '2026-10-10 11:30:00' -> 'by Priya Sharma on 10 Oct 2026, 11:30 IST'
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const last_change_text = (last) => {
  if (!last) return 'Never changed (hidden by default).';
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(last.changed_at_ist || '');
  const when = m
    ? ` on ${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]} IST`
    : '';
  const who = (last.changed_by && (last.changed_by.name || last.changed_by.id)) || 'unknown';
  const what = Number(last.earnings_visible) === 1 ? 'Turned on' : 'Turned off';
  return `${what} by ${who}${when}${last.reason ? `: ${last.reason}` : ''}`;
};
